import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { caregiverProfilesAPI, caregiverFilesAPI, caregiverDocumentsAPI, assignmentsAPI, visitsAPI, clientsAPI, usersAPI } from '../services/api';
import FormSubmissions from '../components/FormSubmissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  UserCircle, ArrowLeft, Phone, MapPin, Certificate, Briefcase,
  Calendar, UploadSimple, File as FileIcon, FilePdf, FileImage, FileDoc,
  Trash, DownloadSimple, Plus, PencilSimple, Note, Eye, ShieldCheck, Users, EnvelopeSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import CaregiverLinkCheck from '../components/CaregiverLinkCheck';

const CaregiverProfilePage = () => {
  const { caregiverId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isDocCreateOpen, setIsDocCreateOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docForm, setDocForm] = useState({ title: '', doc_type: 'note', content: '' });
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isPayRateOpen, setIsPayRateOpen] = useState(false);
  const [payRateForm, setPayRateForm] = useState({ pay_rate: '', overtime_pay_rate: '', weekend_pay_rate: '' });
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [deactivateForm, setDeactivateForm] = useState({ reason: '', effective_date: format(new Date(), 'yyyy-MM-dd') });
  const [processingStatus, setProcessingStatus] = useState(false);
  const [isCreateLoginOpen, setIsCreateLoginOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [creatingLogin, setCreatingLogin] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetForm, setResetForm] = useState({ new_password: '', confirm: '' });
  const [resettingPw, setResettingPw] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!profile?.id) return;
    if (deleteConfirmText !== profile.name) {
      toast.error('Please type the caregiver name exactly to confirm');
      return;
    }
    setDeleting(true);
    try {
      const res = await caregiverProfilesAPI.delete(profile.id);
      toast.success(res.data?.message || `${profile.name} permanently deleted`);
      navigate('/caregivers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete caregiver');
    } finally { setDeleting(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (resetForm.new_password !== resetForm.confirm) { toast.error('Passwords do not match'); return; }
    if (!profile?.user_id) { toast.error('No login linked to this caregiver'); return; }
    setResettingPw(true);
    try {
      await usersAPI.resetPassword(profile.user_id, resetForm.new_password);
      toast.success(`Password reset — ${profile.name} will be prompted to change it on next sign-in`);
      setIsResetPasswordOpen(false);
      setResetForm({ new_password: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally { setResettingPw(false); }
  };

  const openCreateLogin = () => {
    setLoginForm({ email: '', password: '' });
    setIsCreateLoginOpen(true);
  };

  const handleCreateLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) { toast.error('Email and password are required'); return; }
    if (loginForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setCreatingLogin(true);
    try {
      await caregiverProfilesAPI.createLogin(profile.id, loginForm);
      toast.success(`Login created for ${profile.name} — they can now be assigned to shifts`);
      setIsCreateLoginOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create login');
    } finally { setCreatingLogin(false); }
  };

  const handleDeactivate = async () => {
    if (!profile?.id) return;
    setProcessingStatus(true);
    try {
      const res = await caregiverProfilesAPI.deactivate(profile.id, deactivateForm);
      toast.success(`Caregiver deactivated — ${res.data.future_visits_unassigned || 0} future visits unassigned`);
      setIsDeactivateOpen(false);
      setDeactivateForm({ reason: '', effective_date: format(new Date(), 'yyyy-MM-dd') });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    } finally { setProcessingStatus(false); }
  };

  const handleReactivate = async () => {
    if (!profile?.id) return;
    if (!window.confirm('Reactivate this caregiver? They will be able to log in again and be assigned to visits.')) return;
    setProcessingStatus(true);
    try {
      await caregiverProfilesAPI.reactivate(profile.id);
      toast.success('Caregiver reactivated');
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally { setProcessingStatus(false); }
  };

  useEffect(() => { loadAllData(); }, [caregiverId]);

  const loadAllData = async () => {
    try {
      const [profileRes, filesRes, docsRes, assignRes, visitsRes, clientsRes] = await Promise.all([
        caregiverProfilesAPI.getById(caregiverId),
        caregiverFilesAPI.list(caregiverId),
        caregiverDocumentsAPI.list(caregiverId),
        assignmentsAPI.getAll({ caregiver_id: profile?.user_id || '' }),
        visitsAPI.getAll({ caregiver_id: profile?.user_id || '' }),
        clientsAPI.getAll()
      ]);
      setProfile(profileRes.data);
      setFiles(filesRes.data);
      setDocuments(docsRes.data);
      setAssignments(assignRes.data);
      setVisits(visitsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error loading caregiver profile:', error);
      toast.error('Failed to load caregiver profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be less than 10MB'); return; }
    setUploading(true);
    try {
      await caregiverFilesAPI.upload(caregiverId, file);
      toast.success(`Uploaded: ${file.name}`);
      const res = await caregiverFilesAPI.list(caregiverId);
      setFiles(res.data);
    } catch (error) { toast.error('Failed to upload file'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleFileDownload = async (fileRecord) => {
    try {
      const response = await caregiverFilesAPI.download(fileRecord.id);
      const blob = new Blob([response.data], { type: fileRecord.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = fileRecord.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error('Failed to download file'); }
  };

  const handleFileDelete = async (fileId) => {
    try { await caregiverFilesAPI.delete(fileId); toast.success('File deleted'); setFiles(files.filter(f => f.id !== fileId)); }
    catch (error) { toast.error('Failed to delete file'); }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      if (editingDoc) {
        await caregiverDocumentsAPI.update(editingDoc.id, { ...docForm, caregiver_id: caregiverId });
        toast.success('Document updated');
      } else {
        await caregiverDocumentsAPI.create({ ...docForm, caregiver_id: caregiverId });
        toast.success('Document created');
      }
      setIsDocCreateOpen(false); setEditingDoc(null); setDocForm({ title: '', doc_type: 'note', content: '' });
      const res = await caregiverDocumentsAPI.list(caregiverId);
      setDocuments(res.data);
    } catch (error) { toast.error(editingDoc ? 'Failed to update document' : 'Failed to create document'); }
  };

  const handleEditDocument = (doc) => { setEditingDoc(doc); setDocForm({ title: doc.title, doc_type: doc.doc_type, content: doc.content }); setIsDocCreateOpen(true); };
  const handleDeleteDocument = async (docId) => { try { await caregiverDocumentsAPI.delete(docId); toast.success('Document deleted'); setDocuments(documents.filter(d => d.id !== docId)); } catch (error) { toast.error('Failed to delete document'); } };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...editForm,
        certifications: typeof editForm.certifications === 'string' ? editForm.certifications.split(',').map(s => s.trim()).filter(Boolean) : editForm.certifications,
        specializations: typeof editForm.specializations === 'string' ? editForm.specializations.split(',').map(s => s.trim()).filter(Boolean) : editForm.specializations
      };
      await caregiverProfilesAPI.update(caregiverId, data);
      toast.success('Profile updated');
      setIsEditProfileOpen(false);
      const res = await caregiverProfilesAPI.getById(caregiverId);
      setProfile(res.data);
    } catch (error) { toast.error('Failed to update profile'); }
  };

  const openEditProfile = () => {
    setEditForm({
      ...profile,
      certifications: profile.certifications?.join(', ') || '',
      specializations: profile.specializations?.join(', ') || ''
    });
    setIsEditProfileOpen(true);
  };

  const openPayRates = () => {
    setPayRateForm({
      pay_rate: profile.pay_rate || '',
      overtime_pay_rate: profile.overtime_pay_rate || '',
      weekend_pay_rate: profile.weekend_pay_rate || ''
    });
    setIsPayRateOpen(true);
  };

  const handleUpdatePayRates = async (e) => {
    e.preventDefault();
    try {
      await caregiverProfilesAPI.updatePayRates(caregiverId, {
        pay_rate: parseFloat(payRateForm.pay_rate) || 0,
        overtime_pay_rate: payRateForm.overtime_pay_rate ? parseFloat(payRateForm.overtime_pay_rate) : null,
        weekend_pay_rate: payRateForm.weekend_pay_rate ? parseFloat(payRateForm.weekend_pay_rate) : null
      });
      toast.success('Pay rates updated');
      setIsPayRateOpen(false);
      const res = await caregiverProfilesAPI.getById(caregiverId);
      setProfile(res.data);
    } catch (error) { toast.error('Failed to update pay rates'); }
  };

  const getFileIcon = (ct) => { if (ct?.includes('pdf')) return FilePdf; if (ct?.includes('image')) return FileImage; if (ct?.includes('word')) return FileDoc; return FileIcon; };
  const formatFileSize = (b) => { if (!b) return 'N/A'; if (b < 1024) return `${b} B`; if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`; return `${(b / (1024 * 1024)).toFixed(1)} MB`; };
  const getDocTypeColor = (t) => { switch (t) { case 'note': return 'bg-blue-100 text-blue-800'; case 'evaluation': return 'bg-green-100 text-green-800'; case 'training': return 'bg-purple-100 text-purple-800'; case 'incident_report': return 'bg-red-100 text-red-800'; case 'certification': return 'bg-yellow-100 text-yellow-800'; default: return 'bg-gray-100 text-gray-800'; } };
  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.name || 'Unknown';

  if (user?.role !== 'supervisor') return <div className="flex flex-col items-center justify-center h-64"><UserCircle weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg">Access Restricted</h3></div>;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!profile) return <div className="text-center py-12"><p className="text-muted-foreground">Caregiver not found</p><Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate('/caregivers')}>Back</Button></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="caregiver-profile-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" className="rounded-xl gap-1" onClick={() => navigate('/caregivers')} data-testid="back-to-caregivers-btn">
          <ArrowLeft weight="bold" className="w-4 h-4" /> Back
        </Button>
      </div>

      {/* Profile Header */}
      <Card className="rounded-2xl border border-border" data-testid="cg-profile-header">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-blue-600/10 flex items-center justify-center shrink-0">
              <UserCircle weight="duotone" className="w-10 h-10 text-blue-600" />
            </div>
            <div className="flex-1 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-semibold">{profile.name}</h1>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {profile.hire_date && <p className="text-muted-foreground text-sm flex items-center gap-1"><Calendar weight="duotone" className="w-4 h-4" /> Hired: {profile.hire_date}</p>}
                    <Badge className={`rounded-full text-xs ${profile.employment_status === 'active' ? 'bg-green-100 text-green-800' : profile.employment_status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                      {profile.employment_status?.replace('_', ' ')}
                    </Badge>
                    {profile.status === 'inactive' && (
                      <Badge className="rounded-full text-xs bg-red-100 text-red-800" data-testid="inactive-badge">
                        INACTIVE{profile.deactivation_reason ? ` — ${profile.deactivation_reason}` : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" className="rounded-xl gap-2" onClick={openEditProfile} data-testid="edit-profile-btn">
                    <PencilSimple weight="bold" className="w-4 h-4" /> Edit Profile
                  </Button>
                  {(!profile.user_id || profile.user_id === 'none' || profile.user_id === '') ? (
                    <Button variant="outline" className="rounded-xl gap-2 border-primary text-primary hover:bg-primary/10" onClick={openCreateLogin} data-testid="create-login-btn">
                      Create Login
                    </Button>
                  ) : (
                    <Button variant="outline" className="rounded-xl gap-2" onClick={() => setIsResetPasswordOpen(true)} data-testid="reset-password-btn">
                      Reset Password
                    </Button>
                  )}
                  {profile.status === 'inactive' ? (
                    <>
                      <Button variant="outline" className="rounded-xl gap-2 text-green-700 border-green-600 hover:bg-green-50" onClick={handleReactivate} data-testid="reactivate-caregiver-btn">
                        Reactivate
                      </Button>
                      <Button variant="outline" className="rounded-xl gap-2 text-destructive border-destructive hover:bg-destructive/10" onClick={() => { setDeleteConfirmText(''); setIsDeleteOpen(true); }} data-testid="delete-caregiver-btn">
                        <Trash weight="bold" className="w-4 h-4" /> Delete Permanently
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" className="rounded-xl gap-2 text-destructive border-destructive hover:bg-destructive/10" onClick={() => setIsDeactivateOpen(true)} data-testid="deactivate-caregiver-btn">
                      Deactivate
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {profile.email && <div className="flex items-center gap-2 text-sm" data-testid="caregiver-profile-email"><EnvelopeSimple weight="duotone" className="w-4 h-4 text-muted-foreground" /><a href={`mailto:${profile.email}`} className="text-primary hover:underline truncate">{profile.email}</a></div>}
                {profile.phone && <div className="flex items-center gap-2 text-sm"><Phone weight="duotone" className="w-4 h-4 text-muted-foreground" /><span className="text-muted-foreground">{profile.phone}</span></div>}
                {profile.address && <div className="flex items-start gap-2 text-sm"><MapPin weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5" /><span className="text-muted-foreground">{profile.address}</span></div>}
                {profile.emergency_contact && <div className="flex items-start gap-2 text-sm"><Phone weight="duotone" className="w-4 h-4 text-destructive mt-0.5" /><span className="text-muted-foreground"><strong>Emergency:</strong> {profile.emergency_contact} {profile.emergency_phone && `- ${profile.emergency_phone}`}</span></div>}
              </div>

              <div className="flex flex-wrap gap-4">
                {profile.certifications?.length > 0 && (
                  <div className="flex items-start gap-2"><Certificate weight="duotone" className="w-4 h-4 text-muted-foreground mt-1" /><div className="flex flex-wrap gap-1">{profile.certifications.map((c, i) => <Badge key={i} variant="secondary" className="text-xs rounded-full">{c}</Badge>)}</div></div>
                )}
                {profile.specializations?.length > 0 && (
                  <div className="flex items-start gap-2"><Briefcase weight="duotone" className="w-4 h-4 text-muted-foreground mt-1" /><div className="flex flex-wrap gap-1">{profile.specializations.map((s, i) => <Badge key={i} className="text-xs rounded-full bg-blue-100 text-blue-800">{s}</Badge>)}</div></div>
                )}
              </div>
              {profile.notes && <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-xl italic">{profile.notes}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deactivate Caregiver Dialog */}
      <Dialog open={isDeactivateOpen} onOpenChange={setIsDeactivateOpen}>        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Deactivate Caregiver</DialogTitle>
            <DialogDescription>
              This will disable {profile?.name}'s login, unassign them from all future scheduled visits, and mark their profile inactive. Visit history and completed visits are preserved. You can reactivate them at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Effective Date *</Label>
              <Input type="date" value={deactivateForm.effective_date} onChange={(e) => setDeactivateForm({ ...deactivateForm, effective_date: e.target.value })} className="rounded-xl" data-testid="deactivate-effective-date" />
              <p className="text-xs text-muted-foreground">Visits scheduled on or after this date will be unassigned.</p>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={deactivateForm.reason} onChange={(e) => setDeactivateForm({ ...deactivateForm, reason: e.target.value })} placeholder="e.g. Resigned, Terminated, Leave of absence..." className="rounded-xl" rows={3} data-testid="deactivate-reason" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsDeactivateOpen(false)} disabled={processingStatus}>Cancel</Button>
              <Button className="flex-1 rounded-xl bg-destructive hover:bg-destructive/90" onClick={handleDeactivate} disabled={processingStatus} data-testid="confirm-deactivate-btn">
                {processingStatus ? 'Deactivating...' : 'Confirm Deactivation'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Caregiver Permanently Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Permanently Delete Caregiver</DialogTitle>
            <DialogDescription>
              This <strong>cannot be undone</strong>. It will remove {profile?.name}'s profile, login, files, documents, and all assignments. Past visits will be preserved for billing/audit — their name is kept as a snapshot on each visit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Type <span className="font-mono font-semibold">{profile?.name}</span> to confirm</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={profile?.name}
                className="rounded-xl"
                data-testid="delete-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsDeleteOpen(false)} disabled={deleting}>Cancel</Button>
              <Button
                className="flex-1 rounded-xl bg-destructive hover:bg-destructive/90 gap-2"
                onClick={handleDelete}
                disabled={deleting || deleteConfirmText !== profile?.name}
                data-testid="confirm-delete-btn"
              >
                <Trash weight="bold" className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Caregiver Login Dialog */}
      <Dialog open={isCreateLoginOpen} onOpenChange={setIsCreateLoginOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Login for {profile?.name}</DialogTitle>
            <DialogDescription>
              This caregiver profile doesn't have a user account, so they can't be assigned to shifts yet.
              Set an email + password to create their login now. They'll be able to sign in, clock in/out, and receive visit assignments.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="caregiver@example.com" className="rounded-xl" required data-testid="cg-login-email" />
            </div>
            <div className="space-y-2">
              <Label>Temporary Password *</Label>
              <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Minimum 6 characters" className="rounded-xl" required minLength={6} data-testid="cg-login-password" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateLoginOpen(false)} disabled={creatingLogin}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={creatingLogin} data-testid="cg-login-submit">
                {creatingLogin ? 'Creating...' : 'Create Login'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset Password for {profile?.name}</DialogTitle>
            <DialogDescription>
              Set a temporary password. {profile?.name} will be required to change it on next sign-in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input type="password" value={resetForm.new_password} onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })} className="rounded-xl" required minLength={6} data-testid="reset-new-password" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password *</Label>
              <Input type="password" value={resetForm.confirm} onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })} className="rounded-xl" required minLength={6} data-testid="reset-confirm-password" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsResetPasswordOpen(false)} disabled={resettingPw}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={resettingPw} data-testid="confirm-reset-password-btn">
                {resettingPw ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Edit Profile Dialog */}
      <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Caregiver Profile</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateProfile} className="space-y-4 mt-4">
            <div className="space-y-2"><Label>Full Name *</Label><Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="rounded-xl" data-testid="edit-cg-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={editForm.date_of_birth || ''} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-2"><Label>Hire Date</Label><Input type="date" value={editForm.hire_date || ''} onChange={(e) => setEditForm({ ...editForm, hire_date: e.target.value })} className="rounded-xl" /></div>
            </div>
            <div className="space-y-2"><Label>Phone</Label><Input value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>Address</Label><Textarea value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Emergency Contact</Label><Input value={editForm.emergency_contact || ''} onChange={(e) => setEditForm({ ...editForm, emergency_contact: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-2"><Label>Emergency Phone</Label><Input value={editForm.emergency_phone || ''} onChange={(e) => setEditForm({ ...editForm, emergency_phone: e.target.value })} className="rounded-xl" /></div>
            </div>
            <div className="space-y-2"><Label>Certifications (comma-separated)</Label><Input value={editForm.certifications || ''} onChange={(e) => setEditForm({ ...editForm, certifications: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>Specializations (comma-separated)</Label><Input value={editForm.specializations || ''} onChange={(e) => setEditForm({ ...editForm, specializations: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>Employment Status</Label>
              <Select value={editForm.employment_status || 'active'} onValueChange={(v) => setEditForm({ ...editForm, employment_status: v })}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="on_leave">On Leave</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="rounded-xl" /></div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsEditProfileOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" data-testid="save-cg-profile-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50 rounded-xl p-1">
          <TabsTrigger value="overview" className="rounded-lg" data-testid="cg-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="files" className="rounded-lg" data-testid="cg-tab-files">Files ({files.length})</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg" data-testid="cg-tab-documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="assignments" className="rounded-lg" data-testid="cg-tab-assignments">Assignments</TabsTrigger>
          <TabsTrigger value="forms" className="rounded-lg" data-testid="cg-tab-forms">Forms</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6">
          <CaregiverLinkCheck profileId={profile.id} onRelinked={loadAllData} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="rounded-2xl border border-border"><CardContent className="p-6 text-center"><p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Uploaded Files</p><h3 className="font-heading text-3xl font-semibold mt-2">{files.length}</h3></CardContent></Card>
            <Card className="rounded-2xl border border-border"><CardContent className="p-6 text-center"><p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Documents</p><h3 className="font-heading text-3xl font-semibold mt-2">{documents.length}</h3></CardContent></Card>
            <Card className="rounded-2xl border border-border"><CardContent className="p-6 text-center"><p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Certifications</p><h3 className="font-heading text-3xl font-semibold mt-2">{profile.certifications?.length || 0}</h3></CardContent></Card>
          </div>

          {/* Pay Rates Card */}
          <Card className="rounded-2xl border border-border" data-testid="cg-pay-rates-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Pay Rates</CardTitle>
                <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={openPayRates} data-testid="edit-pay-rates-btn">
                  <PencilSimple weight="bold" className="w-3 h-3" /> Set Rates
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-muted/30 text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Standard Rate</p>
                  <p className="font-heading text-2xl font-semibold mt-1 text-blue-700">${(profile.pay_rate || 0).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span></p>
                </div>
                <div className="p-4 rounded-xl bg-muted/30 text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Overtime Rate</p>
                  <p className="font-heading text-2xl font-semibold mt-1 text-orange-700">${(profile.overtime_pay_rate || (profile.pay_rate || 0) * 1.5).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span></p>
                </div>
                <div className="p-4 rounded-xl bg-muted/30 text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Weekend Rate</p>
                  <p className="font-heading text-2xl font-semibold mt-1 text-purple-700">${(profile.weekend_pay_rate || (profile.pay_rate || 0) * 1.25).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span></p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pay Rate Edit Dialog */}
          <Dialog open={isPayRateOpen} onOpenChange={setIsPayRateOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Set Pay Rates</DialogTitle></DialogHeader>
              <form onSubmit={handleUpdatePayRates} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Standard Pay Rate ($/hr) *</Label>
                  <Input type="number" step="0.01" min="0" value={payRateForm.pay_rate} onChange={(e) => setPayRateForm({ ...payRateForm, pay_rate: e.target.value })} placeholder="18.00" required className="rounded-xl" data-testid="cg-pay-rate-input" />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Rate ($/hr) <span className="text-xs text-muted-foreground">(default: 1.5x)</span></Label>
                  <Input type="number" step="0.01" min="0" value={payRateForm.overtime_pay_rate} onChange={(e) => setPayRateForm({ ...payRateForm, overtime_pay_rate: e.target.value })} placeholder="27.00" className="rounded-xl" data-testid="cg-ot-pay-rate-input" />
                </div>
                <div className="space-y-2">
                  <Label>Weekend Rate ($/hr) <span className="text-xs text-muted-foreground">(default: 1.25x)</span></Label>
                  <Input type="number" step="0.01" min="0" value={payRateForm.weekend_pay_rate} onChange={(e) => setPayRateForm({ ...payRateForm, weekend_pay_rate: e.target.value })} placeholder="22.50" className="rounded-xl" data-testid="cg-wknd-pay-rate-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsPayRateOpen(false)}>Cancel</Button>
                  <Button type="submit" className="flex-1 rounded-xl" data-testid="save-pay-rates-btn">Save Rates</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* FILES */}
        <TabsContent value="files" className="space-y-6">
          <div className="flex items-center justify-between">
            <div><h2 className="font-heading text-xl font-semibold">Uploaded Files</h2><p className="text-sm text-muted-foreground">Certifications, training records, documents</p></div>
            <div>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.csv,.xlsx" data-testid="cg-file-upload-input" />
              <Button className="rounded-xl gap-2" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="cg-upload-file-btn">
                <UploadSimple weight="bold" className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </div>
          </div>
          {files.length === 0 ? (
            <Card className="rounded-2xl border border-border"><CardContent className="flex flex-col items-center justify-center py-12"><FileIcon weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg font-medium">No files uploaded</h3></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {files.map(file => { const Icon = getFileIcon(file.content_type); return (
                <Card key={file.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`cg-file-card-${file.id}`}>
                  <CardContent className="p-4"><div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0"><div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center shrink-0"><Icon weight="duotone" className="w-6 h-6 text-blue-600" /></div>
                    <div className="min-w-0"><p className="font-medium text-sm truncate">{file.original_filename}</p><p className="text-xs text-muted-foreground">{formatFileSize(file.size)} &middot; {format(new Date(file.created_at), 'MMM d, yyyy')}</p></div></div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => handleFileDownload(file)} data-testid={`cg-download-${file.id}`}><DownloadSimple weight="bold" className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => handleFileDelete(file.id)} data-testid={`cg-delete-file-${file.id}`}><Trash weight="bold" className="w-4 h-4" /></Button>
                    </div>
                  </div></CardContent>
                </Card>
              ); })}
            </div>
          )}
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="space-y-6">
          <div className="flex items-center justify-between">
            <div><h2 className="font-heading text-xl font-semibold">Documents</h2><p className="text-sm text-muted-foreground">Evaluations, training notes, incident reports</p></div>
            <Dialog open={isDocCreateOpen} onOpenChange={(open) => { setIsDocCreateOpen(open); if (!open) { setEditingDoc(null); setDocForm({ title: '', doc_type: 'note', content: '' }); } }}>
              <DialogTrigger asChild><Button className="rounded-xl gap-2" data-testid="cg-create-doc-btn"><Plus weight="bold" className="w-4 h-4" /> Create Document</Button></DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">{editingDoc ? 'Edit Document' : 'Create Document'}</DialogTitle><DialogDescription>{editingDoc ? 'Update the document' : 'Create a new document'}</DialogDescription></DialogHeader>
                <form onSubmit={handleCreateDocument} className="space-y-4 mt-4">
                  <div className="space-y-2"><Label>Title *</Label><Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} required className="rounded-xl" data-testid="cg-doc-title" /></div>
                  <div className="space-y-2"><Label>Type *</Label>
                    <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                      <SelectTrigger className="rounded-xl" data-testid="cg-doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="note">General Note</SelectItem><SelectItem value="evaluation">Performance Evaluation</SelectItem><SelectItem value="training">Training Record</SelectItem><SelectItem value="incident_report">Incident Report</SelectItem><SelectItem value="certification">Certification Record</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Content *</Label><Textarea value={docForm.content} onChange={(e) => setDocForm({ ...docForm, content: e.target.value })} required rows={10} className="rounded-xl" data-testid="cg-doc-content" /></div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => { setIsDocCreateOpen(false); setEditingDoc(null); }}>Cancel</Button>
                    <Button type="submit" className="flex-1 rounded-xl" data-testid="cg-save-doc-btn">{editingDoc ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Dialog open={!!viewingDoc} onOpenChange={(open) => { if (!open) setViewingDoc(null); }}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              {viewingDoc && (<><DialogHeader><DialogTitle className="font-heading">{viewingDoc.title}</DialogTitle><DialogDescription>By {viewingDoc.created_by_name} &middot; {format(new Date(viewingDoc.created_at), 'MMM d, yyyy h:mm a')}</DialogDescription></DialogHeader><div className="mt-4 p-4 rounded-xl bg-muted/30 whitespace-pre-wrap text-sm">{viewingDoc.content}</div></>)}
            </DialogContent>
          </Dialog>

          {documents.length === 0 ? (
            <Card className="rounded-2xl border border-border"><CardContent className="flex flex-col items-center justify-center py-12"><Note weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg font-medium">No documents yet</h3></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => (
                <Card key={doc.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`cg-doc-card-${doc.id}`}>
                  <CardContent className="p-4"><div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center shrink-0 mt-1"><Note weight="duotone" className="w-6 h-6 text-blue-600" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1"><p className="font-medium text-sm">{doc.title}</p><Badge className={`${getDocTypeColor(doc.doc_type)} rounded-full text-xs`}>{doc.doc_type.replace('_', ' ')}</Badge></div>
                        <p className="text-xs text-muted-foreground">By {doc.created_by_name} &middot; {format(new Date(doc.created_at), 'MMM d, yyyy')}</p>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{doc.content}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setViewingDoc(doc)} data-testid={`cg-view-doc-${doc.id}`}><Eye weight="bold" className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => handleEditDocument(doc)} data-testid={`cg-edit-doc-${doc.id}`}><PencilSimple weight="bold" className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => handleDeleteDocument(doc.id)} data-testid={`cg-delete-doc-${doc.id}`}><Trash weight="bold" className="w-4 h-4" /></Button>
                    </div>
                  </div></CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ASSIGNMENTS */}
        <TabsContent value="assignments" className="space-y-6">
          <h2 className="font-heading text-xl font-semibold">Client Assignments</h2>
          {assignments.filter(a => a.is_active).length === 0 ? (
            <Card className="rounded-2xl border border-border"><CardContent className="flex flex-col items-center justify-center py-12"><Users weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg font-medium">No active assignments</h3></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {assignments.filter(a => a.is_active).map(a => (
                <Card key={a.id} className="rounded-2xl border border-border" data-testid={`cg-assignment-${a.id}`}>
                  <CardContent className="p-4"><div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><Users weight="duotone" className="w-6 h-6 text-primary" /></div>
                    <div><p className="font-medium text-sm">{getClientName(a.client_id)}</p><p className="text-xs text-muted-foreground">Since {a.start_date}</p></div>
                    <Badge variant="default" className="rounded-full ml-auto">Active</Badge>
                  </div></CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FORMS TAB */}
        <TabsContent value="forms" className="space-y-6">
          <FormSubmissions entityType="caregiver" entityId={caregiverId} userRole={user?.role} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaregiverProfilePage;